import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import {
    Body1,
    Flex,
    FullBleed,
    Heading2,
    Heading3,
    IconLocalShipping,
    Stack,
    Tabs,
} from '@myphx-engineering/phx-design-system';
import { useIsMobile } from '@myphx-engineering/phx-design-system/hooks';

import { configuredDrugKeys } from '../../../constants';
import {
    useMedicationConfigOverride,
    useProgramContentOverride,
} from '../../../contexts/DrugProgramProvider';
import {
    useFillPathway,
    useFillPrescription,
} from '../../../contexts/fill/use-fill-prescription';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import { useDrugKey } from '../../../hooks/use-drug-key';
import { useMedicareBridgeEligibilityStepper } from '../../../hooks/use-medicare-bridge-eligibility-stepper';
import { processProviderToPharmacyInfo } from '../../../util';
import { getMedicationSwitchConfig } from '../../../util/config/switch-config';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { shouldShowEscapeHatch } from '../../../util/should-show-escape-hatch';
import { PharmacyChangeLink } from '../cabinet/provider-offers/PharmacyChangeLink';

import { PharmacyOfferList } from './common/PharmacyOfferList';
import { PricingCard } from './common/PricingCard';
import { MedicareBridgeEligibilityStepper } from './configurable-path-selection/MedicareBridgeEligibilityStepper';

export const SelectPharmacyScreen: FC = () => {
    const { t } = useTranslation();
    const isMobile = useIsMobile('md');
    const { drxId, medication, offers } = useFillPrescription();
    const [searchParams] = useSearchParams();

    const { pathway, subPathway } = useFillPathway();
    const medConfig = useMedicationConfigOverride();
    const medicareBridgeEligibilityContent =
        useMedicareBridgeEligibilityStepper();
    const drugKey = useDrugKey(medication);
    const contentOverride = useProgramContentOverride();

    const showEscapeHatch = drugKey
        ? shouldShowEscapeHatch({
              options: contentOverride.pathwayOptions?.[drugKey],
              pathway,
              subPathway,
          })
        : false;

    const switchConfig = getMedicationSwitchConfig(
        medConfig,
        medication,
        pathway,
        subPathway
    );

    const showMedicareBridgeEligibilityInformation =
        medicareBridgeEligibilityContent &&
        pathway === 'government' &&
        subPathway === 'medicare-bridge';

    if (switchConfig) {
        return (
            <Navigate
                replace
                to={{
                    pathname: getAbsoluteRoute('fill.drxId.switch', { drxId }),
                    search: `?${searchParams.toString()}`,
                }}
            />
        );
    }

    if (!showEscapeHatch && offers.length === 1) {
        const providerInfo = processProviderToPharmacyInfo(
            getFragment(offers[0].provider),
            medConfig
        );

        if (providerInfo) {
            return (
                <Navigate
                    replace
                    to={{
                        pathname: getAbsoluteRoute(
                            'fill.drxId.pharmacy.offer',
                            {
                                drxId,
                                pharmacyId: providerInfo.id,
                                offerId: offers[0].id,
                            }
                        ),
                        search: `?${searchParams.toString()}`,
                    }}
                />
            );
        }
    }

    return (
        <Stack gap="lg" data-testid="pharmacy-select-page-root">
            {showMedicareBridgeEligibilityInformation ? (
                <Stack gap="sm">
                    <Heading2>
                        {medicareBridgeEligibilityContent.heading}
                    </Heading2>
                    <MedicareBridgeEligibilityStepper
                        options={medicareBridgeEligibilityContent.cards}
                        infoPanel={medicareBridgeEligibilityContent.infoPanel}
                    />
                </Stack>
            ) : (
                <PricingCard />
            )}

            <Heading2>{t('fill.selectPharmacy.heading')}</Heading2>

            {!!drugKey && configuredDrugKeys.includes(drugKey) ? (
                <FullBleed breakpoint="md" color="primary" border="top">
                    <Stack
                        gap="md"
                        data-testid="pharmacy-select-available-pharmacies"
                    >
                        <Heading3>
                            {t('fill.selectPharmacy.availablePharmacies')}
                        </Heading3>

                        <PharmacyOfferList
                            drxId={drxId}
                            offers={offers}
                            isConfiguredDrug
                            showEscapeHatch={showEscapeHatch}
                        />
                    </Stack>
                </FullBleed>
            ) : (
                <Tabs
                    defaultValue="delivery"
                    grow
                    tabBarProps={{ fullBleed: isMobile }}
                    tabs={[
                        {
                            label: t('pickYourPharmacy.delivery'),
                            value: 'delivery',
                            LeftIcon: IconLocalShipping,
                            panel: (
                                <FullBleed breakpoint="md" color="primary">
                                    <Stack
                                        gap="md"
                                        data-testid="pharmacy-select-available-pharmacies"
                                    >
                                        <Flex
                                            justify="space-between"
                                            align="center"
                                        >
                                            <Body1>
                                                {t(
                                                    'providerOffers.pharmacies',
                                                    {
                                                        count: offers.length,
                                                    }
                                                )}
                                            </Body1>
                                        </Flex>

                                        <PharmacyOfferList
                                            drxId={drxId}
                                            offers={offers}
                                        />
                                        <PharmacyChangeLink drxId={drxId} />
                                    </Stack>
                                </FullBleed>
                            ),
                        },
                    ]}
                />
            )}
        </Stack>
    );
};
